/**
 * Evaluate a Copier `when:` condition against the current answers, so the
 * wizard can show/hide conditional questions.
 *
 * Copier `when` values are Jinja expressions. We support the common boolean
 * subset used in practice: variable references, `not`, `and`, `or`, parentheses,
 * `true`/`false`, `==`/`!=` against booleans/strings, and `in`/`not in` (list
 * membership, or substring when the right side is a string). Anything we can't
 * parse defaults to VISIBLE (true) — the backend's real copier run, plus its
 * cross-field validator, remain the source of truth.
 */
type Answers = Record<string, unknown>;

function truthy(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.length > 0 && v !== 'false';
  if (typeof v === 'number') return v !== 0;
  return v != null;
}

/** Jinja `in`: membership in a list, substring of a string, false for anything else. */
function isMember(needle: unknown, haystack: unknown): boolean {
  if (Array.isArray(haystack)) return haystack.includes(needle);
  if (typeof haystack === 'string') return typeof needle === 'string' && haystack.includes(needle);
  return false;
}

export function evaluateWhen(when: string | undefined, answers: Answers): boolean {
  if (!when) return true;
  const expr = when.replace(/\{\{|\}\}|\{%|%\}/g, '').trim();
  if (!expr) return true;

  try {
    const tokens = expr.match(
      /\(|\)|==|!=|\b(?:and|or|not|in|true|false)\b|[A-Za-z_]\w*|'[^']*'|"[^"]*"/g,
    );
    if (!tokens) return true;

    let pos = 0;
    const peek = (): string | undefined => tokens[pos];
    const next = (): string | undefined => tokens[pos++];

    const parseOr = (): boolean => {
      let left = parseAnd();
      while (peek() === 'or') {
        next();
        const right = parseAnd();
        left = left || right;
      }
      return left;
    };
    const parseAnd = (): boolean => {
      let left = parseNot();
      while (peek() === 'and') {
        next();
        const right = parseNot();
        left = left && right;
      }
      return left;
    };
    const parseNot = (): boolean => {
      if (peek() === 'not') {
        next();
        return !parseNot();
      }
      return parseComparison();
    };
    const parseComparison = (): boolean => {
      const left = parseAtom();
      const op = peek();
      if (op === '==' || op === '!=') {
        next();
        const right = parseAtom();
        const eq = left.raw === right.raw;
        return op === '==' ? eq : !eq;
      }
      // `'x' in var` / `'x' not in var`: `not` binds to `in` here, not to the left side.
      const negated = op === 'not' && tokens[pos + 1] === 'in';
      if (op === 'in' || negated) {
        next();
        if (negated) next();
        const right = parseAtom();
        const member = isMember(left.raw, right.raw);
        return negated ? !member : member;
      }
      return left.bool;
    };
    const parseAtom = (): { bool: boolean; raw: unknown } => {
      const tok = next();
      if (tok === undefined) return { bool: true, raw: undefined };
      if (tok === '(') {
        const v = parseOr();
        if (peek() === ')') next();
        return { bool: v, raw: v };
      }
      if (tok === 'true') return { bool: true, raw: true };
      if (tok === 'false') return { bool: false, raw: false };
      if (tok.startsWith("'") || tok.startsWith('"')) {
        const raw = tok.slice(1, -1);
        return { bool: raw.length > 0, raw };
      }
      const value = answers[tok];
      return { bool: truthy(value), raw: value };
    };

    const result = parseOr();
    // Leftover tokens mean the expression used syntax beyond our boolean subset
    // (filters, attribute access, …) — default to VISIBLE rather than guess.
    if (pos < tokens.length) return true;
    return result;
  } catch {
    return true;
  }
}
